import React, { useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import ProductBookingsTable from './ProductBookingsTable'
import ProductBookingsCalendar from './ProductBookingsCalendar'
import Dropdown from '../../../../../../../../../../../../../components/Dropdown'
import TableDataLoader from '../../../../../../../../../../../../../components/Table/TableDataLoader'

import { ReactComponent as AlertIcon } from '../../../../../../../../../../../../../assets/icons/alertIcon.svg'
import { AddProductsBulkContext } from '../../../../../../AddProductsBulkContext'

import { getBookedMediaReportService } from '../../../../../../../../../../../../../modules/services/mediaOrdersBookings'
import { selectedControllerDataSelector } from '../../../../../../../../../../../../../modules/selectors/app'

import { formatDateToBE } from '../../../../../../../../../../../../../constants/dates'
import { bookingsColumnsSchema } from './ProductBookingsTable/const/columns'

import { red } from '../../../../../../../../../../../../../styles/const/colors'

import useStyles from './styles'
import useBookingsTableStyles from './ProductBookingsTable/styles'

const NoInventoryDropdown = ({ productValues }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const bookingTableClasses = useBookingsTableStyles()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const selectedController = useSelector(selectedControllerDataSelector)
  const supportCustomDates = selectedController?.custom_booking_dates
  const selectedControllerId = selectedController.id

  const { dateRange } = useContext(AddProductsBulkContext)

  const productData = productValues.data
  const productId = productData.id

  const { data: bookedMedias, isLoading: getBookedMediaIsLoading } = useQuery({
    queryKey: ['bookedMedia_by_product', productId, selectedControllerId, dateRange.startDate, dateRange.endDate],
    queryFn: async () =>
      getBookedMediaReportService({
        controller: selectedControllerId,
        ordering: 'name',
        media_product: productId,
        // for custom dates the periods could be selected few days before or after periods based on before_percentage
        ...(supportCustomDates
          ? {
              date_end_after: formatDateToBE(dateRange.startDate),
              date_start_before: formatDateToBE(dateRange.endDate)
            }
          : // for not custom dates the periods could be selected only within the selected date range
            {
              date_start_after: formatDateToBE(dateRange.startDate),
              date_end_before: formatDateToBE(dateRange.endDate)
            })
      }),
    enabled: isDropdownOpen,
    retry: false
  })

  const hasBookings = bookedMedias?.length > 0

  const isFormLoading = useMemo(() => !bookedMedias || getBookedMediaIsLoading, [bookedMedias, getBookedMediaIsLoading])
  const onVisibilityChange = useCallback(state => {
    setIsDropdownOpen(state)
  }, [])

  return (
    <Dropdown
      className={classes.dropdownBody}
      placement="left"
      borderWidth={2}
      borderColor={red}
      portaled={true}
      withFloatingArrow={true}
      // to avoid jumping of the dropdown when the calendar is opened:
      allowPlacementFlip={false}
      onOpenChange={onVisibilityChange}
      hideOnReferenceDisappear={true}
      triggerElement={<AlertIcon className={classes.competingErrorIcon} />}
    >
      <div className={classes.dropdownBodyWrapper}>
        {hasBookings && (
          <>
            <h4>{t('Existing Bookings')}</h4>
            <p className={classes.description}>
              {t('The following confirmed bookings exist during the selected period')}
            </p>
            <ProductBookingsCalendar bookedMedias={bookedMedias} productData={productData} dateRange={dateRange} />
          </>
        )}
        <TableDataLoader
          loadersClassName={bookingTableClasses.competingTable}
          itemsLength={bookedMedias?.length}
          errorSelector={() => null}
          wasLoadedSelector={() => !!bookedMedias}
          isLoadingSelector={() => isFormLoading}
          skeletonProps={{ cols: bookingsColumnsSchema }}
          noDataContent={
            <div className={classes.noPeriods}>
              {t('There are no bookings. Inventory may not exist or it has been set to 0 for the selected period.')}
            </div>
          }
        >
          <ProductBookingsTable bookings={bookedMedias} />
        </TableDataLoader>
      </div>
    </Dropdown>
  )
}

NoInventoryDropdown.propTypes = {
  productValues: PropTypes.object
}

export default NoInventoryDropdown
